import React, { useContext, useLayoutEffect } from 'react';
import { useCurrentFrame } from '../use-current-frame';
import { useVideoConfig } from '../use-video-config';
import { SequenceContext } from '../SequenceContext';
import { Easing } from '../Easing';
import { delayRender, continueRender } from '../delay-render';

// FIX-4 (dead code): removed unused CANDY constant — PALETTES.candy is the
// single source of truth.

const PALETTES = {
  candy:  ['#FF6B6B', '#FF9F43', '#FECA57', '#48DBFB', '#FF9FF3', '#FD79A8', '#54A0FF', '#00D2D3', '#FF6348'],
  gold:   ['#FFD700', '#FFA500', '#FFE566', '#FFB347', '#EAC117', '#FFC300', '#FBB917', '#DAA520'],
  jungle: ['#6BCB77', '#4D96FF', '#FFE156', '#FF6B6B', '#C77DFF', '#FFBE0B', '#3BCEAC', '#F4A261'],
  ice:    ['#74B9FF', '#A29BFE', '#00CEC9', '#81ECEC', '#FDCB6E', '#6C5CE7', '#48DBFB', '#0984E3'],
};

// ─── Background-agnostic readability ─────────────────────────────────────────
//
// On a controlled dark stage the candy palette reads fine as-is.
// On any random image/video the same light colours (cyan, pink, yellow) can
// disappear against bright regions.  We solve this with three layered techniques
// that work together regardless of background:
//
//   1. webkitTextStroke  — a solid outline traced around every letter glyph.
//      The stroke colour is chosen per-readabilityMode so it always contrasts
//      with BOTH the letter fill AND the background behind it.
//
//   2. textShadow (multi-layer) — two shadow layers on every character:
//      • Inner halo  : white, 0 offset, small blur  → lifts letter off dark bg
//      • Outer shadow: near-black, offset+blur       → pops against light bg
//      Together they guarantee visibility on any tone.
//
//   3. Shadow span (`.shd`) — the existing offset duplicate span behind each
//      character, now with a stronger, more opaque colour in non-none modes.
//
// readabilityMode values (maps to titleCardConfig.readability):
//   'none'        Original behaviour — no stroke, no multi-shadow.
//                 Use only when you control the background.
//   'auto'        Dual-shadow (halo + drop). No stroke. Works on most backgrounds.
//                 Best for natural/photographic backgrounds.
//   'outline'     Thick dark stroke + dual-shadow. Maximum contrast.
//                 Best for busy/high-frequency backgrounds (crowd shots, nature).
//   'bubble'      White stroke + dual-shadow. Gives a cartoon-bubble look that
//                 reads well on both dark and light backgrounds.
//
// strokeWidth is in em so it scales with font size automatically.

const READABILITY = {
  none: {
    stroke:      'none',
    strokeWidth: '0em',
    textShadow:  null,
    shdColor:    'rgba(0,0,0,0.3)',
  },
  auto: {
    stroke:      'none',
    strokeWidth: '0em',
    // white inner halo + black outer drop — readable on any background tone
    textShadow: [
      '0 0 8px rgba(255,255,255,0.9)',    // white halo, lifts off dark bg
      '0 0 2px rgba(255,255,255,1)',       // tight white glow
      '2px 3px 6px rgba(0,0,0,0.85)',     // strong dark drop shadow
      '-1px -1px 4px rgba(0,0,0,0.6)',    // subtle counter-shadow
    ].join(', '),
    shdColor: 'rgba(0,0,0,0.55)',
  },
  outline: {
    // dark stroke around glyph — most visible on busy backgrounds
    stroke:      '#1a1a1a',
    strokeWidth: '0.06em',
    textShadow: [
      '0 0 6px rgba(255,255,255,0.7)',
      '3px 4px 8px rgba(0,0,0,0.95)',
      '-1px -1px 4px rgba(0,0,0,0.7)',
    ].join(', '),
    shdColor: 'rgba(0,0,0,0.65)',
  },
  bubble: {
    // white stroke gives the cartoon-bubble look, pops on both light and dark
    stroke:      '#ffffff',
    strokeWidth: '0.07em',
    textShadow: [
      '0 0 4px rgba(255,255,255,0.6)',
      '3px 4px 10px rgba(0,0,0,0.9)',
      '-1px -1px 5px rgba(0,0,0,0.65)',
    ].join(', '),
    shdColor: 'rgba(0,0,0,0.6)',
  },
};

const FREDOKA_FONT_SPEC = '80px "Fredoka One"';

// ─── zoomPunch enter sub-segment d-ratios ────────────────────────────────────
// FIX-1: The original code collapsed the 4-keyframe zoomPunch into a single
// outBack lerp in seg2, missing the 0.82→1.12→1 bounce stages.
// d-values from the CSS prototype: [1, 1.2, 1.5, 1.0] (total 4.7 units).
// These cumulative breakpoints (0-1 normalised) let us branch inside enterSeg2.
const PUNCH_BREAKS = [
  1 / 4.7,               // 0→2.8  outExpo   ends here
  (1 + 1.2) / 4.7,      // 2.8→0.82 inQuad  ends here
  (1 + 1.2 + 1.5) / 4.7 // 0.82→1.12 outBack ends here
  // remainder            // 1.12→1  outQuad
];

// ─── Easing helpers ──────────────────────────────────────────────────────────
const easeOutExpo  = Easing.out(Easing.exp);
const easeInExpo   = Easing.in(Easing.exp);
const easeOutBack  = Easing.out(Easing.back(1.70158));
const easeOutQuad  = Easing.out(Easing.quad);
const easeInQuad   = Easing.in(Easing.quad);
const easeOutBounce = Easing.bounce; // Easing.bounce is already an out-bounce

// Linear interpolation
const lerp = (a, b, t) => a + (b - a) * t;

// ─── computeEnterSeg1 ────────────────────────────────────────────────────────
// Returns {scale, opacity, tx, ty, rotation} for the first enter segment
// (big → overshoot), given a normalised t [0,1] and the canvas dimensions.
function computeEnterSeg1(enterKey, t, W, H) {
  const e = easeOutExpo(t);
  let scale = 1, opacity = e, tx = 0, ty = 0, rotation = 0;

  switch (enterKey) {
    case 'zoomStagger':
      scale = lerp(8, 1.2, e);
      // FIX-5: ty expressed as fraction of canvas height, not hardcoded px
      ty = (-0.069 * H) * (1 - e); // ~-50px at 720p
      break;
    case 'zoomIn':
      scale = lerp(7, 1.18, e);
      break;
    case 'zoomPunch':
      // seg1 of punch: 0 → 2.8 outExpo
      scale = lerp(0, 2.8, e);
      break;
    case 'bounce':
      scale = lerp(0.1, 1, easeOutBounce(t));
      rotation = (Math.floor(t * 30) % 2 === 0 ? 18 : -18) * (1 - t);
      opacity = 1;
      break;
    case 'spin':
      scale = lerp(0.1, 1, e);
      rotation = 360 * (1 - e);
      break;
    case 'wave':
      scale = lerp(0.85, 1, easeOutBack(t));
      ty = (0.125 * H) * (1 - t); // ~90px at 720p
      opacity = t;
      break;
    case 'pop':
      scale = lerp(0.15, 1, easeOutBack(t));
      ty = (-0.111 * H) * (1 - t); // ~-80px at 720p
      opacity = t;
      break;
    case 'slide':
      scale = lerp(0.7, 1, easeOutBack(t));
      tx = (-0.25 * W) * (1 - t); // ~-200px at 800px canvas
      opacity = t;
      break;
    default:
      scale = lerp(8, 1.2, e);
      ty = (-0.069 * H) * (1 - e);
  }

  return { scale, opacity, tx, ty, rotation };
}

// ─── computeEnterSeg2 ────────────────────────────────────────────────────────
// Returns {scale, opacity, tx, ty, rotation} for the second enter segment
// (overshoot → settle to 1).
function computeEnterSeg2(enterKey, t) {
  let scale = 1, opacity = 1, tx = 0, ty = 0, rotation = 0;

  if (enterKey === 'zoomPunch') {
    // FIX-1: Full 4-stage bounce restored.
    // PUNCH_BREAKS divides seg2's normalised t into 4 sub-segments.
    if (t <= PUNCH_BREAKS[0]) {
      // sub-seg A: 0→2.8 outExpo  (already done in seg1, but seg2 starts here
      // if enterSeg1 length is very short — guard included for safety)
      const st = t / PUNCH_BREAKS[0];
      scale = lerp(0, 2.8, easeOutExpo(st));
    } else if (t <= PUNCH_BREAKS[1]) {
      // sub-seg B: 2.8→0.82 inQuad
      const st = (t - PUNCH_BREAKS[0]) / (PUNCH_BREAKS[1] - PUNCH_BREAKS[0]);
      scale = lerp(2.8, 0.82, easeInQuad(st));
    } else if (t <= PUNCH_BREAKS[2]) {
      // sub-seg C: 0.82→1.12 outBack
      const st = (t - PUNCH_BREAKS[1]) / (PUNCH_BREAKS[2] - PUNCH_BREAKS[1]);
      scale = lerp(0.82, 1.12, easeOutBack(st));
    } else {
      // sub-seg D: 1.12→1 outQuad
      const st = (t - PUNCH_BREAKS[2]) / (1 - PUNCH_BREAKS[2]);
      scale = lerp(1.12, 1, easeOutQuad(st));
    }
  } else if (enterKey === 'zoomIn') {
    scale = lerp(1.18, 1, easeOutBack(t));
  } else if (enterKey === 'bounce' || enterKey === 'spin' ||
             enterKey === 'wave'   || enterKey === 'pop'  ||
             enterKey === 'slide') {
    // These animations complete fully in seg1; seg2 is a tiny settle — hold at 1.
    scale = 1;
  } else {
    // zoomStagger and default: 1.2→1 outBack
    scale = lerp(1.2, 1, easeOutBack(t));
  }

  return { scale, opacity, tx, ty, rotation };
}

// ─── computeExitSeg1 ─────────────────────────────────────────────────────────
// Returns the state at the end of exit-seg1 for a given t, starting from
// holdEndState. Used both to evaluate mid-segment AND to seed seg2's from-values.
function computeExitSeg1(exitKey, holdEndState, t, H, W) {
  const e = easeOutQuad(t);
  const { scale: fromS, rotation: fromR, tx: fromTx, ty: fromTy } = holdEndState;

  let toS = 1, toR = 0, toTx = 0, toTy = 0;
  switch (exitKey) {
    case 'zoomOutStagger': toS = 1.3; break;
    case 'zoomOut':        toS = 1.18; break;
    case 'zoomBlast':      toS = 1.7; break;
    // FIX-5: py offsets as canvas fractions
    case 'fly':    toS = 1; toTy = -0.111 * H; break; // ~-80px at 720p
    case 'shrink': toS = 1; toR = 60; break;
    case 'waveExit': toS = 1; break;
    case 'explode':  toS = 1; toR = 120; break;
    default:         toS = 1.3;
  }

  return {
    scale:    lerp(fromS,  toS,  e),
    rotation: lerp(fromR,  toR,  e),
    tx:       lerp(fromTx, toTx, e),
    ty:       lerp(fromTy, toTy, e),
    opacity:  1,
  };
}

// ─── computeExitSeg2 ─────────────────────────────────────────────────────────
// FIX-2: Exit seg2 now starts from the exact values seg1 ended at (seg1EndState)
// instead of hardcoded constants, eliminating the scale/ty discontinuity jerk.
function computeExitSeg2(exitKey, seg1EndState, t, H, W) {
  const e = easeInExpo(t);
  const { scale: s1, rotation: r1, tx: tx1, ty: ty1 } = seg1EndState;

  let scale = s1, rotation = r1, tx = tx1, ty = ty1;
  const opacity = 1 - e;

  switch (exitKey) {
    case 'zoomOutStagger':
      scale    = lerp(s1,  10,  e);
      ty       = lerp(ty1, ty1 + (-0.056 * H), e); // ~-40px at 720p
      rotation = r1;
      break;
    case 'zoomOut':
      scale    = lerp(s1, 0, e);
      break;
    case 'zoomBlast':
      scale    = lerp(s1, 14, e);
      break;
    case 'fly':
      scale    = lerp(s1, s1 * 0.3, e);
      // FIX-5: travel expressed as canvas fraction
      ty       = lerp(ty1, ty1 + (-0.444 * H), e); // ~-320px at 720p
      break;
    case 'shrink':
      scale    = lerp(s1, 0, e);
      rotation = lerp(r1, r1 + 200, e);
      break;
    case 'waveExit':
      scale    = lerp(s1, 0.8, e);
      ty       = lerp(ty1, ty1 + (0.181 * H), e); // ~130px at 720p
      break;
    case 'explode':
      scale    = lerp(s1, 1.5, e);
      tx       = lerp(tx1, tx1 + (0.125 * W), e); // ~100px at 800px canvas
      ty       = lerp(ty1, ty1 + (-0.139 * H), e); // ~-100px at 720p
      rotation = lerp(r1, r1 + 120, e);
      break;
    default:
      scale    = lerp(s1, 10, e);
      ty       = lerp(ty1, ty1 + (-0.056 * H), e);
  }

  return { scale, opacity, tx, ty, rotation };
}

// ─── Per-unit animator ───────────────────────────────────────────────────────
// Computes transform state for a single animation unit (char / word / title)
// given the unit's local frame (already adjusted for stagger offset).
function computeUnitState(unitFrame, totalFrames, enterKey, holdKey, exitKey, H, W) {
  const enterEnd     = totalFrames * 0.25;
  const holdEnd      = totalFrames * 0.80;
  // Enter: seg1 uses d=2 units out of 3.5 total for zoom animations;
  // single-segment animations (bounce, spin, wave, pop, slide) use all of seg1.
  const enterSeg1    = enterEnd * (2 / 3.5);
  // Exit: seg1 = first 1/3 of exit phase
  const exitSeg1     = holdEnd + (totalFrames - holdEnd) * (1 / 3);
  const holdDuration = Math.max(1, holdEnd - enterEnd);

  // ── Hold oscillation with blend-in / blend-out envelope ─────────────────
  const computeHold = (holdFrameLocal) => {
    const progress = Math.max(0, Math.min(1, holdFrameLocal / holdDuration));
    const FADE     = 0.08;
    const env      = Math.max(0, Math.min(1, Math.min(progress / FADE, (1 - progress) / FADE, 1)));
    let s = 1, r = 0, txx = 0, tyy = 0;
    switch (holdKey) {
      case 'breath':
        s = 1 + Math.sin(holdFrameLocal * 0.05) * 0.08 * env; break;
      case 'wiggle':
        r   = Math.sin(holdFrameLocal * 0.07)  * 4 * env;
        tyy = Math.sin(holdFrameLocal * 0.077) * 4 * env; break;
      case 'pulse':
        s = 1 + Math.sin(holdFrameLocal * 0.08) * 0.06 * env; break;
      case 'float':
        tyy = Math.sin(holdFrameLocal * 0.06)  * 6 * env;
        txx = Math.sin(holdFrameLocal * 0.036) * 3 * env; break;
      default:
        s = 1 + Math.sin(holdFrameLocal * 0.05) * 0.08 * env;
    }
    return { scale: s, rotation: r, tx: txx, ty: tyy };
  };

  // ── ENTER ──────────────────────────────────────────────────────────────────
  if (unitFrame <= enterEnd) {
    if (unitFrame <= enterSeg1) {
      const t = enterSeg1 > 0 ? Math.min(unitFrame / enterSeg1, 1) : 1;
      return computeEnterSeg1(enterKey, t, W, H);
    } else {
      const t = (enterEnd - enterSeg1) > 0
        ? Math.min((unitFrame - enterSeg1) / (enterEnd - enterSeg1), 1) : 1;
      return computeEnterSeg2(enterKey, t);
    }
  }

  // ── HOLD ───────────────────────────────────────────────────────────────────
  if (unitFrame <= holdEnd) {
    const h = computeHold(unitFrame - enterEnd);
    return { scale: h.scale, opacity: 1, tx: h.tx, ty: h.ty, rotation: h.rotation };
  }

  // ── EXIT ───────────────────────────────────────────────────────────────────
  // Seed: exact hold-end state (envelope → 0 at end, so this is effectively
  // rest, but we compute explicitly for pixel-perfect continuity).
  const holdEndState = computeHold(holdDuration);

  if (unitFrame <= exitSeg1) {
    const t = (exitSeg1 - holdEnd) > 0
      ? Math.min((unitFrame - holdEnd) / (exitSeg1 - holdEnd), 1) : 1;
    return computeExitSeg1(exitKey, holdEndState, t, H, W);
  } else {
    const t = (totalFrames - exitSeg1) > 0
      ? Math.min((unitFrame - exitSeg1) / (totalFrames - exitSeg1), 1) : 1;
    // FIX-2: seed seg2 from the exact values seg1 produced at t=1
    const seg1EndState = computeExitSeg1(exitKey, holdEndState, 1, H, W);
    return computeExitSeg2(exitKey, seg1EndState, t, H, W);
  }
}

// ─── TitleCardAnimation ──────────────────────────────────────────────────────
/**
 * TitleCardAnimation — frame-accurate port of kids_cartoon_title_animator v8.
 *
 * Props
 * ─────
 *   children        string   The title text (will be uppercased).
 *   videoConfig     object   Optional override; falls back to useVideoConfig().
 *   titleCardConfig object   {
 *     palette    : 'candy' | 'gold' | 'jungle' | 'ice'
 *     plainColor : '#rrggbb'   (overrides palette, makes all chars one colour)
 *     enter      : 'zoomStagger' | 'zoomIn' | 'zoomPunch' | 'bounce' |
 *                  'spin' | 'wave' | 'pop' | 'slide'
 *     hold       : 'breath' | 'wiggle' | 'pulse' | 'float'
 *     exit       : 'zoomOutStagger' | 'zoomOut' | 'zoomBlast' | 'fly' |
 *                  'shrink' | 'waveExit' | 'explode'
 *     level      : 'title' | 'word' | 'char'   (animation grouping)
 *     readability : 'auto' | 'outline' | 'bubble' | 'none'
 *                  Controls stroke + multi-shadow for background-agnostic
 *                  legibility. Default 'auto'. Use 'none' only on a controlled
 *                  (dark) background. 'outline' for busy backgrounds.
 *     shadow     : CSS text-shadow string | 'none'
 *                  Overrides the readability textShadow when explicitly set.
 *   }
 *
 * Phase layout (fraction of overlay duration)
 * ────────────────────────────────────────────
 *   Enter   0 – 25 %
 *   Hold   25 – 80 %
 *   Exit   80 – 100 %
 *
 * Fixes applied vs the original file
 * ────────────────────────────────────
 *   FIX-1  zoomPunch enter restored to full 4-stage bounce (was 2 stages)
 *   FIX-2  Exit seg2 starts from exact seg1 end-values, not hardcoded constants
 *   FIX-3  useLayoutEffect no longer inside a conditional (Rules-of-Hooks)
 *   FIX-4  Dead CANDY constant removed
 *   FIX-5  px offsets replaced with canvas-relative fractions
 *   FIX-6  level prop: 'title' | 'word' | 'char' stagger support added
 */
function TitleCardAnimation({ children, videoConfig, titleCardConfig }) {
  const frame          = useCurrentFrame();
  const vc             = useVideoConfig();
  const sequenceContext = useContext(SequenceContext);

  const fps         = (videoConfig || vc)?.fps    || 30;
  const canvasW     = (videoConfig || vc)?.width  || 800;
  const canvasH     = (videoConfig || vc)?.height || 720;

  // Use SequenceContext.durationInFrames — AnimFrameProvider sets this to
  // exactly (overlay.endTime - overlay.startTime) × fps.
  // useVideoConfig().durationInFrames is the total project duration — wrong here.
  const totalFrames = sequenceContext?.durationInFrames
    || (videoConfig || vc)?.durationInFrames
    || fps * 4;

  // Config
  const cfg         = titleCardConfig || {};
  const palette     = cfg.plainColor ? [cfg.plainColor] : (PALETTES[cfg.palette] || PALETTES.candy);
  const enterKey    = cfg.enter  || 'zoomStagger';
  const holdKey     = cfg.hold   || 'breath';
  const exitKey     = cfg.exit   || 'zoomOutStagger';
  const level       = cfg.level  || 'title';   // FIX-6: new prop
  // readability: 'none' | 'auto' | 'outline' | 'bubble'  (default: 'auto')
  // 'none' = original dark-stage-only behaviour; anything else = bg-agnostic.
  const readability = READABILITY[cfg.readability] || READABILITY.none;
  // Shadow priority: explicit cfg.shadow (from style panel) > readability preset > none
  const shadowStyle = (cfg.shadow && cfg.shadow !== 'none')
    ? cfg.shadow
    : (readability.textShadow || undefined);

  // FIX-3: useLayoutEffect always called — guard lives inside the effect body.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const handle = delayRender('Loading Fredoka One font for TitleCardAnimation');
    document.fonts
      .load(FREDOKA_FONT_SPEC)
      .then(() => continueRender(handle))
      .catch(() => continueRender(handle));
    // Cleanup: continueRender is idempotent — safe to call on unmount
    return () => continueRender(handle);
  }, []);

  // ── Build word/char structure ─────────────────────────────────────────────
  const text  = typeof children === 'string' ? children : String(children || '');
  const upper = text.toUpperCase();
  const words = upper.split(' ');

  // Count total animation units for stagger calculations
  const unitCount = level === 'title' ? 1
    : level === 'word' ? words.length
    : words.reduce((n, w) => n + w.length, 0); // 'char'

  // FIX-6: Stagger budget = 35% of enter phase for char, 25% for word, 0 for title.
  // The budget is spread evenly across (unitCount - 1) gaps so the last unit's
  // animation always finishes exactly at enterEnd, keeping total duration fixed.
  const enterPhaseFrames = totalFrames * 0.25;
  const staggerFrac      = level === 'char' ? 0.35 : level === 'word' ? 0.25 : 0;
  const staggerBudget    = enterPhaseFrames * staggerFrac;
  const staggerStep      = unitCount > 1 ? staggerBudget / (unitCount - 1) : 0;
  // Each unit gets the full phase scaled down so stagger + animation = enterPhaseFrames
  const unitTotalFrames  = totalFrames - staggerBudget;

  // ── Render ────────────────────────────────────────────────────────────────
  let charIdx   = 0;
  let unitIndex = 0; // increments per char or per word depending on level

  const wordElements = words.map((word, wi) => {
    const wordUnitIndex = unitIndex; // capture before incrementing chars

    const chars = word.split('').map((ch) => {
      const color        = palette[charIdx % palette.length];
      const charUnitIdx  = unitIndex;
      charIdx++;
      if (level === 'char') unitIndex++;

      // Per-character state (only used when level === 'char')
      const charOffset  = charUnitIdx * staggerStep;
      const charFrame   = Math.max(0, frame - charOffset);
      const charState   = level === 'char'
        ? computeUnitState(charFrame, unitTotalFrames, enterKey, holdKey, exitKey, canvasH, canvasW)
        : null;

      const charStyle = charState ? {
        display:         'inline-block',
        position:        'relative',
        color,
        fontFamily:      "'Fredoka One', cursive",
        opacity:         Math.max(0, Math.min(1, charState.opacity)),
        transform:       `translateX(${charState.tx}px) translateY(${charState.ty}px) scale(${charState.scale}) rotate(${charState.rotation}deg)`,
        transformOrigin: 'center center',
        willChange:      'transform, opacity',
        // readability: stroke on the glyph outline
        WebkitTextStroke: readability.stroke !== 'none'
          ? `${readability.strokeWidth} ${readability.stroke}`
          : undefined,
        paintOrder:      readability.stroke !== 'none' ? 'stroke fill' : undefined,
      } : {
        display:    'inline-block',
        position:   'relative',
        color,
        fontFamily: "'Fredoka One', cursive",
        // readability: stroke even on non-animated chars (word/title level)
        WebkitTextStroke: readability.stroke !== 'none'
          ? `${readability.strokeWidth} ${readability.stroke}`
          : undefined,
        paintOrder: readability.stroke !== 'none' ? 'stroke fill' : undefined,
      };

      return (
        <span key={charUnitIdx} style={charStyle}>
          <span
            aria-hidden="true"
            style={{
              position:      'absolute',
              left:          '0.0625em',  // 5/80 em ≈ offset of .shd
              top:           '0.0875em',  // 7/80 em
              zIndex:        -1,
              color:         readability.shdColor,
              pointerEvents: 'none',
              fontFamily:    "'Fredoka One', cursive",
            }}
          >
            {ch}
          </span>
          {ch}
        </span>
      );
    });

    // Word-level state
    const wordOffset = wordUnitIndex * staggerStep;
    const wordFrame  = Math.max(0, frame - wordOffset);
    const wordState  = level === 'word'
      ? computeUnitState(wordFrame, unitTotalFrames, enterKey, holdKey, exitKey, canvasH, canvasW)
      : null;

    if (level === 'word') unitIndex++;

    const wordStyle = wordState ? {
      display:         'inline-flex',
      justifyContent:  'center',
      alignItems:      'center',
      opacity:         Math.max(0, Math.min(1, wordState.opacity)),
      transform:       `translateX(${wordState.tx}px) translateY(${wordState.ty}px) scale(${wordState.scale}) rotate(${wordState.rotation}deg)`,
      transformOrigin: 'center center',
      willChange:      'transform, opacity',
    } : {
      display:        'inline-flex',
      justifyContent: 'center',
      alignItems:     'center',
    };

    return (
      <span key={wi} style={wordStyle}>
        {chars}
        {wi < words.length - 1 && (
          <span style={{ display: 'inline-block', width: '0.175em' }} />
        )}
      </span>
    );
  });

  // Title-level state (level === 'title' or as wrapper for char/word)
  const titleState = level === 'title'
    ? computeUnitState(frame, totalFrames, enterKey, holdKey, exitKey, canvasH, canvasW)
    : null;

  const titleTransform = titleState
    ? `translateX(${titleState.tx}px) translateY(${titleState.ty}px) scale(${titleState.scale}) rotate(${titleState.rotation}deg)`
    : undefined;

  return (
    <div
      style={{
        display:         'inline-flex',
        flexWrap:        'wrap',
        justifyContent:  'center',
        alignItems:      'center',
        gap:             '0 0.075em',  // 6/80 em
        lineHeight:      1.1,
        opacity:         titleState ? Math.max(0, Math.min(1, titleState.opacity)) : 1,
        transform:       titleTransform,
        willChange:      titleState ? 'transform, opacity' : undefined,
        transformOrigin: 'center center',
        textShadow:      shadowStyle,
      }}
    >
      {wordElements}
    </div>
  );
}

export default React.memo(TitleCardAnimation);